import React, { useRef } from 'react';
import classNames from 'classnames';
import { useField, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import GenericFormElement from '../GenericFormElement';
import Button from '../../Button';
import Icon from '../../Icon';
import IconButton from '../../IconButton';
import useAriaDescribedByField from '../../../hooks/useAriaDescribedByField';
import { calculateTotalFileSize, fileMimeMapper, formatAcceptAttribute, formatExtensionsDescription, getMimeExtension } from './helpers';
const FileUpload = _ref => {
  var _field$value, _field$value3, _field$value4;
  let {
    label,
    name,
    maxFileCount,
    maxTotalFileSize = 5,
    allowedFileMimes,
    allowCamera,
    buttonText,
    optionalLabel,
    isLiveValidationDisabled
  } = _ref;
  const {
    t
  } = useTranslation();
  const inputRef = useRef(null);
  const [field, meta, helpers] = useField(name);
  const {
    submitCount
  } = useFormikContext();
  const fileSizeTotal = calculateTotalFileSize(field.value || []);
  const acceptAttribute = formatAcceptAttribute(allowedFileMimes, allowCamera);
  const {
    ariaDescribedBy
  } = useAriaDescribedByField({
    field,
    meta,
    description: true
  });
  const classes = classNames('FileUpload');
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(GenericFormElement, {
    name: name,
    meta: meta,
    label: label,
    description: /*#__PURE__*/React.createElement(Description, {
      maxFileCount: maxFileCount,
      maxTotalFileSize: maxTotalFileSize,
      allowedFileMimes: allowedFileMimes
    }),
    helpers: helpers,
    className: classes,
    optionalLabel: optionalLabel,
    submitCount: submitCount,
    isLiveValidationDisabled: isLiveValidationDisabled
  }, /*#__PURE__*/React.createElement(Button, {
    type: "secondary",
    icon: "add",
    disabled: ((_field$value = field.value) === null || _field$value === void 0 ? void 0 : _field$value.length) >= maxFileCount,
    onClick: () => {
      var _inputRef$current;
      return (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 ? void 0 : _inputRef$current.click();
    },
    "aria-describedby": ariaDescribedBy
  }, buttonText), /*#__PURE__*/React.createElement("input", {
    ref: inputRef,
    id: name,
    className: "hidden",
    name: name,
    type: "file",
    multiple: maxFileCount > 1,
    accept: acceptAttribute,
    onChange: evt => {
      var _evt$currentTarget$fi, _field$value2;
      helpers.setTouched(true);
      // Return if number of files uploaded exceeds the limit.
      if ((((_evt$currentTarget$fi = evt.currentTarget.files) === null || _evt$currentTarget$fi === void 0 ? void 0 : _evt$currentTarget$fi.length) || 0) + (((_field$value2 = field.value) === null || _field$value2 === void 0 ? void 0 : _field$value2.length) || 0) > maxFileCount) {
        helpers.setError(maxFileCount === 1 ? t('form.field.file_upload.amount.error_singular') : t('form.field.file_upload.amount.error_plural', {
          maxAmount: maxFileCount
        }));
        return;
      }
      if (!evt.currentTarget.files) {
        // Something went wrong with the file upload.
        helpers.setError(t('form.field.file_upload.error'));
        return;
      }
      const fileArray = Array.from(evt.currentTarget.files);
      const compressedFiles = [];
      const newFileSizeTotal = calculateTotalFileSize(fileArray) + fileSizeTotal;
      if (newFileSizeTotal > maxTotalFileSize) {
        helpers.setError(t('form.field.file_upload.size.error', {
          maxSize: maxTotalFileSize
        }));
        return;
      }
      fileArray.forEach(file => {
        // Check if the filename ends with ".jfif" if so, fail the file type check.
        const filename = file.name;
        const jfifExtension = filename.substring(filename.length - 5) === '.jfif';

        // Check file type.
        if (!allowedFileMimes.includes(fileMimeMapper[file.type]) || jfifExtension) {
          helpers.setError(t('form.field.file_upload.type.error', {
            mimeTypes: formatExtensionsDescription(allowedFileMimes)
          }));
          return;
        }
        compressedFiles.push(file);
        const newFiles = [...(field.value || []), ...compressedFiles];

        // Set the files in formik.
        helpers.setValue(newFiles);
      });

      // Reset file list.
      evt.currentTarget.files = null;
    }
  })), ((_field$value3 = field.value) === null || _field$value3 === void 0 ? void 0 : _field$value3.length) > 0 && /*#__PURE__*/React.createElement("ul", {
    className: "FileUpload__file-list"
  }, (_field$value4 = field.value) === null || _field$value4 === void 0 ? void 0 : _field$value4.map((file, i) => /*#__PURE__*/React.createElement("li", {
    className: "FileUpload__file"
  }, file.type.startsWith('image') ? /*#__PURE__*/React.createElement("img", {
    src: URL.createObjectURL(file),
    alt: ""
  }) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "FileUpload__file-icon"
  }, /*#__PURE__*/React.createElement(Icon, {
    icon: "draft"
  }), /*#__PURE__*/React.createElement("div", {
    className: "ext"
  }, getMimeExtension(fileMimeMapper[file.type], false))), /*#__PURE__*/React.createElement("p", {
    className: "text-muted mb-0"
  }, file.name)), /*#__PURE__*/React.createElement(IconButton, {
    simple: true,
    icon: "delete",
    onClick: () => {
      const newFiles = [...field.value];
      newFiles.splice(i, 1);

      // Remove image from Formik.
      helpers.setValue(newFiles);
    }
  }, t('form.field.file_upload.delete'))))));
};
function Description(props) {
  const {
    maxFileCount,
    allowedFileMimes,
    maxTotalFileSize
  } = props;
  const {
    t
  } = useTranslation();
  return /*#__PURE__*/React.createElement("ul", null, /*#__PURE__*/React.createElement("li", null, t('form.field.file_upload.type.description', {
    mimeTypes: formatExtensionsDescription(allowedFileMimes)
  })), /*#__PURE__*/React.createElement("li", null, t('form.field.file_upload.size.description', {
    maxSize: maxTotalFileSize
  })), /*#__PURE__*/React.createElement("li", null, maxFileCount === 1 ? t('form.field.file_upload.amount.description_singular') : t('form.field.file_upload.amount.description_plural', {
    maxAmount: maxFileCount
  })));
}
export default FileUpload;